import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const FMCG_CATEGORIES = [
  "Grocery & Staples",
  "Rice, Flour & Grains",
  "Pulses & Lentils",
  "Spices & Masala",
  "Edible Oils & Ghee",
  "Sugar, Salt & Jaggery",
  "Tea, Coffee & Beverages",
  "Juices & Soft Drinks",
  "Snacks & Chips",
  "Biscuits & Cookies",
  "Bakery",
  "Breakfast & Cereals",
  "Instant Food / Ready-to-Eat",
  "Sauces, Spreads & Pickles",
  "Chocolates & Confectionery",
  "Dairy (Milk, Curd, Paneer)",
  "Frozen Foods",
  "Personal Care",
  "Skin Care",
  "Hair Care",
  "Oral Care",
  "Men's Grooming",
  "Feminine Hygiene",
  "Baby Care",
  "Health & Nutrition",
  "Home Care / Household",
  "Laundry & Detergents",
  "Dishwash",
  "Cleaning Supplies",
  "Paper & Tissue",
  "Pet Care",
  "Others",
];

/* ── Bootstrap numbered pagination ── */
function Pagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>
        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
                disabled={disabled}
              >
                {p}
              </button>
            </li>
          ),
        )}
        <li
          className={`page-item ${page >= totalPages || disabled ? "disabled" : ""}`}
        >
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  /* ── filter state: separate input vs applied ── */
  const [nameInput, setNameInput] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [appliedCategory, setAppliedCategory] = useState("");

  const activeFilterCount = [appliedName, appliedCategory].filter(
    Boolean,
  ).length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / limit)),
    [total, limit],
  );

  const [form, setForm] = useState({
    name: "",
    description: "",
    images: "",
    category: "",
    tags: "",
    originalPrice: "",
    offeredPrice: "",
    stock: "",
  });

  const adminToken = localStorage.getItem("adminToken");

  const resetForm = () =>
    setForm({
      name: "",
      description: "",
      images: "",
      category: "",
      tags: "",
      originalPrice: "",
      offeredPrice: "",
      stock: "",
    });

  const openAdd = () => {
    setEditProduct(null);
    resetForm();
    setImageFiles([]);
    setImageUrls([]);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p?.name || "",
      description: p?.description || "",
      category: p?.category || "",
      tags: Array.isArray(p?.tags) ? p.tags.join(",") : "",
      originalPrice: p?.originalPrice ?? "",
      offeredPrice: p?.offeredPrice ?? "",
      stock: p?.stock ?? "",
    });
    setImageFiles([]);
    setImageUrls(Array.isArray(p?.images) ? p.images : []);
    setShowModal(true);
  };

  const getPublicUrlFromPresigned = (u) => (!u ? "" : u.split("?")[0]);

  const parsePresignedResponse = (result) => {
    const data = result?.data || result;
    let items = data?.urls || [];
    if (!Array.isArray(items)) items = [];
    return items.map((x) => {
      const uploadUrl =
        x?.signedUrl || x?.uploadUrl || x?.url || x?.presignedUrl;
      const publicUrl =
        x?.key ||
        x?.fileUrl ||
        x?.publicUrl ||
        getPublicUrlFromPresigned(uploadUrl);
      return { uploadUrl, publicUrl };
    });
  };

  const uploadSelectedImages = async () => {
    if (!imageFiles.length) return [];
    setUploading(true);
    try {
      const res = await fetch(`${API_BASE}/PresignedUrl/GeneratePresignedUrl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken,
        },
        body: JSON.stringify({
          folder: "products/images",
          fileNames: imageFiles.map((f) => f.name),
        }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to generate presigned URLs");
      }
      const result = await res.json();
      const presignedList = parsePresignedResponse(result);
      const uploadedPublicUrls = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const { uploadUrl, publicUrl } = presignedList[i] || {};
        if (!uploadUrl) continue;
        const put = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed for ${file.name}`);
        uploadedPublicUrls.push(
          publicUrl || getPublicUrlFromPresigned(uploadUrl),
        );
      }
      setImageUrls((prev) => [...prev, ...uploadedPublicUrls]);
      setImageFiles([]);
      return uploadedPublicUrls;
    } finally {
      setUploading(false);
    }
  };

  /* ── FETCH PRODUCTS ── */
  const fetchProducts = async (
    nextPage = 1,
    name = appliedName,
    category = appliedCategory,
  ) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
      });
      if (name.trim()) qs.set("name", name.trim());
      if (category) qs.set("category", category);

      const res = await fetch(
        `${API_BASE}/Product/GetAllProductsWithFilter?${qs.toString()}`,
        { headers: { Authorization: adminToken } },
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      setProducts(result?.data?.products || []);
      setTotal(result?.data?.total || result?.total || 0);
      setPage(nextPage);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  /* initial load */
  useEffect(() => {
    fetchProducts(1, "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedName(nameInput);
    setAppliedCategory(categoryInput);
    setPage(1);
    fetchProducts(1, nameInput, categoryInput);
  };

  const handleClear = () => {
    setNameInput("");
    setAppliedName("");
    setCategoryInput("");
    setAppliedCategory("");
    setPage(1);
    fetchProducts(1, "", "");
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchProducts(p, appliedName, appliedCategory);
  };

  /* ── DELETE ── */
  const handleDelete = async (productId) => {
    const ok = window.confirm(`Delete product ${productId}?`);
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE}/Product/DeleteProduct/${productId}`,
        {
          method: "DELETE",
          headers: { Authorization: adminToken },
        },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Delete failed");
      }
      fetchProducts(page, appliedName, appliedCategory);
    } catch (err) {
      alert(err?.message || "Delete failed");
    }
  };

  /* ── ADD / UPDATE ── */
  const handleSave = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        images: imageUrls,
        category: form.category.trim(),
        tags: form.tags
          ? form.tags
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : [],
        originalPrice: Number(form.originalPrice),
        offeredPrice: Number(form.offeredPrice),
        stock: Number(form.stock),
      };
      const isEdit = !!editProduct?.productId;
      const url = isEdit
        ? `${API_BASE}/Product/UpdateProduct`
        : `${API_BASE}/Product/AddProduct`;
      const method = isEdit ? "PATCH" : "POST";
      const body = isEdit
        ? { productId: editProduct.productId, ...payload }
        : payload;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Save failed");
      }
      setShowModal(false);
      setEditProduct(null);
      resetForm();
      fetchProducts(page, appliedName, appliedCategory);
    } catch (err) {
      alert(err?.message || "Save failed");
    }
  };

  return (
    <>
      <Header />
      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    All Products ({total || products.length})
                  </h3>
                  <div className="text-muted">
                    Manage Products / Add / Update / Delete
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-primary rounded-pill px-5 fw-medium"
                    onClick={openAdd}
                  >
                    Add Product
                  </button>
                  <div
                    className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                    onClick={() =>
                      fetchProducts(page, appliedName, appliedCategory)
                    }
                    role="button"
                    title="Refresh"
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm text-dark" />
                    ) : (
                      <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    {/* Name search */}
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-box-seam me-1 text-muted" />
                        Product Name
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={nameInput}
                          onChange={(e) => setNameInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by product name…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    {/* Category dropdown */}
                    <div className="col-12 col-md-4">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-tag me-1 text-muted" />
                        Category
                      </label>
                      <select
                        className="form-select"
                        value={categoryInput}
                        onChange={(e) => setCategoryInput(e.target.value)}
                        disabled={loading}
                        style={{ fontSize: 14 }}
                      >
                        <option value="">All Categories</option>
                        {FMCG_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Buttons */}
                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={
                          loading ||
                          (!nameInput &&
                            !appliedName &&
                            !categoryInput &&
                            !appliedCategory)
                        }
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* Table */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm" />
                            </td>
                          </tr>
                        ) : products.length === 0 ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5 text-muted"
                            >
                              No products found.
                            </td>
                          </tr>
                        ) : (
                          products.map((p, idx) => (
                            <tr key={p?.productId || idx}>
                              <td className="fw-semibold">
                                {(page - 1) * limit + idx + 1}.
                              </td>
                              <td>
                                <div className="lh-sm my-2">
                                  <div>
                                    <span className="text-muted">
                                      Product ID
                                    </span>{" "}
                                    —{" "}
                                    <span className="fw-semibold">
                                      {p.productId}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">Name</span> —{" "}
                                    {p.name}
                                  </div>
                                  <div>
                                    <span className="text-muted">Category</span>{" "}
                                    — {p.category || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Original Price
                                    </span>{" "}
                                    — ₹{p.originalPrice ?? 0}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Offered Price
                                    </span>{" "}
                                    — ₹{p.offeredPrice ?? 0}
                                  </div>
                                  <div>
                                    <span className="text-muted">Stock</span> —{" "}
                                    {p.stock ?? 0}
                                  </div>
                                  <div>
                                    <span className="text-muted">Tags</span> —{" "}
                                    {Array.isArray(p.tags)
                                      ? p.tags.join(", ")
                                      : "-"}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 align-items-start">
                                  <button
                                    className="btn btn-success btn-sm px-4 fw-medium rounded-pill"
                                    style={{ minWidth: 140 }}
                                    onClick={() => openEdit(p)}
                                  >
                                    Update
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm px-4 fw-medium rounded-pill"
                                    style={{ minWidth: 140 }}
                                    onClick={() => handleDelete(p.productId)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {products.length ? (page - 1) * limit + 1 : 0}–
                  {Math.min(page * limit, total)} of {total} products
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>

              {/* ── MODAL ── */}
              {showModal ? (
                <div className="modal d-block bg-dark bg-opacity-50">
                  <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {editProduct ? "Update Product" : "Add Product"}
                        </h5>
                        <button
                          className="btn-close"
                          onClick={() => setShowModal(false)}
                        />
                      </div>
                      <div className="modal-body">
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label mb-1">
                              Name{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <input
                              className="form-control"
                              value={form.name}
                              onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label mb-1">
                              Category{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={form.category}
                              onChange={(e) =>
                                setForm({ ...form, category: e.target.value })
                              }
                            >
                              <option value="">Select category</option>
                              {FMCG_CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-12">
                            <label className="form-label mb-1">
                              Description{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={form.description}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  description: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label mb-1">
                              Tags (comma){" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <input
                              className="form-control"
                              value={form.tags}
                              onChange={(e) =>
                                setForm({ ...form, tags: e.target.value })
                              }
                              placeholder="nutrition, health, combo"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label mb-1">Images</label>
                            <input
                              type="file"
                              className="form-control"
                              multiple
                              accept="image/*"
                              onChange={(e) =>
                                setImageFiles(Array.from(e.target.files || []))
                              }
                            />
                            <div className="d-flex gap-1 mt-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm rounded-pill px-4"
                                disabled={!imageFiles.length || uploading}
                                onClick={uploadSelectedImages}
                              >
                                {uploading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Uploading...
                                  </>
                                ) : (
                                  "Upload Images"
                                )}
                              </button>
                              {imageUrls.length ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm rounded-pill px-4"
                                  onClick={() => setImageUrls([])}
                                  disabled={uploading}
                                >
                                  Clear Uploaded
                                </button>
                              ) : null}
                            </div>
                            {imageUrls.length ? (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {imageUrls.map((url, i) => (
                                  <div
                                    key={url + i}
                                    className="border rounded-3 p-1"
                                  >
                                    <img
                                      src={url}
                                      alt="product"
                                      style={{
                                        width: 90,
                                        height: 70,
                                        objectFit: "cover",
                                        borderRadius: 8,
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted small mt-2">
                                No uploaded images yet.
                              </div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <label className="form-label mb-1">
                              Original Price{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={form.originalPrice}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  originalPrice: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label mb-1">
                              Offered Price{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={form.offeredPrice}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  offeredPrice: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label mb-1">
                              Stock{" "}
                              <span className="fw-bold text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={form.stock}
                              onChange={(e) =>
                                setForm({ ...form, stock: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary rounded-3 px-5"
                          onClick={() => setShowModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary rounded-3 px-5 m-0"
                          onClick={handleSave}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminProducts;
